import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  IndianRupee, ShoppingCart, Package, AlertTriangle, Wallet, Receipt,
  TrendingUp, Landmark, GraduationCap, ShoppingBag, CreditCard, Landmark as LandmarkIcon,
} from "lucide-react";
import { api, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatCard, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const ONBOARD_STEPS = [
  ["Pick your company", "Use the company switcher in the top bar. Each company is a fully separate workspace."],
  ["Add parties", "Create customers and suppliers under Parties — they power invoices, payments and ledgers."],
  ["Add items", "Set up products/materials/services with GST rates and stock levels."],
  ["Bill & collect", "Create GST invoices, record payments, allocate them, and track receivables."],
  ["Learn as you go", "Every module has a guide in Help & Learning. Press the ? icon anytime."],
];

function Onboarding({ onDone }) {
  const done = async () => {
    await api.post("/onboarding/complete").catch(() => {});
    onDone();
  };
  return (
    <Dialog open onOpenChange={done}>
      <DialogContent aria-describedby={undefined} className="max-w-md bg-white dark:bg-slate-900" data-testid="onboarding-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-amber-500" /> Welcome to Krishna Group BMS
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {ONBOARD_STEPS.map(([t, b], i) => (
            <div key={t} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-bold">{i + 1}</span>
              <div>
                <p className="text-sm font-semibold">{t}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{b}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button data-testid="onboarding-done-btn" onClick={done} className="flex-1 bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Got it — let's start</Button>
          <Button data-testid="onboarding-help-btn" variant="outline" onClick={() => { done(); window.location.href = "/help"; }}>Open guides</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const { activeId, active } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [activeId]);

  const accent = active?.accent || "#D97706";
  const trend = Array.isArray(data?.trend) ? data.trend : [];
  const recentInvoices = Array.isArray(data?.recent_invoices) ? data.recent_invoices : [];

  return (
    <div data-testid="dashboard-page">
      {user && !user.onboarding_done && <Onboarding onDone={() => setUser({ ...user, onboarding_done: true })} />}
      <PageHeader title="Dashboard" testId="dashboard-title"
        sub={`${active?.name || ""} · FY ${data?.fy || ""}`}>
        <Button data-testid="dash-new-invoice" onClick={() => nav("/invoices/new")}
          className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">New Invoice</Button>
      </PageHeader>

      {loading ? <TableSkeleton rows={4} /> : !data ? null : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard testId="stat-sales" title="Total Sales" value={inr(data.sales_total)} accent={accent}
              sub={`${data.invoice_count} posted invoices`} icon={ShoppingCart} onClick={() => nav("/invoices")} />
            <StatCard testId="stat-receivables" title="Receivables" value={inr(data.receivables)} accent="#DC2626"
              sub="Outstanding from customers" icon={IndianRupee} onClick={() => nav("/payments?tab=receivables")} />
            <StatCard testId="stat-collected" title="Collected" value={inr(data.collected)} accent="#059669"
              sub="Payments received" icon={Wallet} onClick={() => nav("/payments")} />
            <StatCard testId="stat-stock" title="Stock Value" value={inr(data.stock_value)} accent="#2563EB"
              sub={`${data.item_count} items · ${data.low_stock} low`} icon={Package} onClick={() => nav("/items")} />
            <StatCard testId="stat-gst" title="GST Collected" value={inr(data.gst_collected)} accent="#7C3AED"
              sub="Output tax on sales" icon={Receipt} />
            <StatCard testId="stat-advances" title="Customer Advances" value={inr(data.advances)} accent="#0891B2"
              sub="Unapplied credits" icon={Landmark} onClick={() => nav("/payments?tab=unallocated")} />
            <StatCard testId="stat-unallocated" title="Unallocated Payments" value={inr(data.unallocated)} accent="#D97706"
              sub="Awaiting invoice matching" icon={AlertTriangle} onClick={() => nav("/payments?tab=unallocated")} />
            <StatCard testId="stat-parties" title="Parties" value={data.party_count} accent="#0F172A"
              sub="Customers & suppliers" icon={TrendingUp} onClick={() => nav("/parties")} />
            <StatCard testId="stat-purchases" title="Purchases" value={inr(data.purchases_total || 0)} accent="#B45309"
              sub="Supplier bills posted" icon={ShoppingBag} onClick={() => nav("/purchases")} />
            <StatCard testId="stat-payables" title="Payables" value={inr(data.payables || 0)} accent="#BE123C"
              sub="Owed to suppliers" icon={CreditCard} onClick={() => nav("/purchases?tab=payables")} />
            <StatCard testId="stat-expenses" title="Expenses" value={inr(data.expenses_total || 0)} accent="#475569"
              sub="Approved spend" icon={Receipt} onClick={() => nav("/expenses")} />
            <StatCard testId="stat-cash-bank" title="Cash & Bank" value={inr(data.cash_bank_balance || 0)} accent="#0E7490"
              sub="Across all accounts" icon={Landmark} onClick={() => nav("/cash-bank")} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Card className="lg:col-span-7 border-slate-200/80 dark:border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-base">Sales vs Collections</CardTitle>
              </CardHeader>
              <CardContent data-testid="sales-trend-chart">
                {trend.length === 0 ? (
                  <p className="py-16 text-center text-sm text-slate-400">Post your first invoice to see trends</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={trend} margin={{ left: 0, right: 8, top: 8 }}>
                      <defs>
                        <linearGradient id="gSales" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="gCol" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#059669" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f022" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                      <Area type="monotone" dataKey="sales" stroke={accent} strokeWidth={2} fill="url(#gSales)" name="Sales" />
                      <Area type="monotone" dataKey="collected" stroke="#059669" strokeWidth={2} fill="url(#gCol)" name="Collected" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-5 border-slate-200/80 dark:border-slate-800">
              <CardHeader className="pb-2 flex-row items-center justify-between">
                <CardTitle className="font-display text-base">Recent Invoices</CardTitle>
                <Button variant="ghost" size="sm" data-testid="view-all-invoices" onClick={() => nav("/invoices")}>View all</Button>
              </CardHeader>
              <CardContent className="p-0">
                {recentInvoices.length === 0 ? (
                  <EmptyState testId="dash-empty-invoices" title="No invoices yet"
                    body="Create your first GST invoice to get started." actionLabel="New Invoice" actionTo="/invoices/new" />
                ) : (
                  <div data-testid="recent-invoices-list">
                    {recentInvoices.map((inv) => (
                      <button key={inv.id} data-testid={`recent-invoice-${inv.invoice_no}`}
                        onClick={() => nav(`/invoices/${inv.id}`)}
                        className="flex w-full items-center justify-between gap-3 border-b border-slate-50 dark:border-slate-800/60 px-5 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 last:border-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium font-mono truncate">{inv.invoice_no}</p>
                          <p className="text-xs text-slate-500 truncate">{inv.party_name} · {fmtDate(inv.date)}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <Money value={inv.total} className="text-sm font-semibold block" />
                          <StatusBadge status={inv.status === "cancelled" ? "Cancelled" : inv.status === "draft" ? "Draft" : inv.balance <= 0 ? "Paid" : inv.paid > 0 ? "Partially Paid" : "Pending"} />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
